/// <amd-module name="Test/FailedTestsMetricView/ControllerViews/FailedTestsMetricView" />
import { VisualTemplate } from "Analytics/Shared/Visuals/Common/Components/VisualTemplate";
import * as React from "react";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { isShowFlakyTestsDetailsEnabled } from "Test/Analytics/Common/Utils/FeatureFlagUtils";
import { FailedTestsMetricActionsCreator } from "Test/FailedTestsMetricView/Actions/FailedTestsMetricActionsCreator";
import * as Resources from "Test/FailedTestsMetricView/Resources";
import { FailedTestsMetricStore, IFailedTestsMetricState } from "Test/FailedTestsMetricView/Stores/FailedTestsMetricStore";
import { localeFormat } from "VSS/Core/Util/String";
import { Components, css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface IFailedTestsMetricProps extends IVssComponentProperties {
    store: FailedTestsMetricStore;
    actionCreator: FailedTestsMetricActionsCreator;
    testReportContext: CommonTypes.ITestReportContext;
}

export class FailedTestsMetricView extends VssComponent<IFailedTestsMetricProps, IFailedTestsMetricState> {
    constructor(props: IFailedTestsMetricProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
    }

    public componentDidMount() {
        super.componentDidMount();
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getState());
        }
    }

    public componentWillUnmount() {
        if (this.props) {
            this.props.store.removeChangedListener(this._onStoreUpdate);
        }
    }

    public render(): JSX.Element {
        return (
            <VisualTemplate
                className={css("top-failing-tests-section", this.props.className || "")}
                title={Resources.FailingTestsText}
                titleId="failing-tests-label"
                tabIndex={0}
                arialabelledby={"failing-tests-label failing-tests-count"}
            >
                {this._getElementToDisplay()}
            </VisualTemplate>
        );
    }

    private _getElementToDisplay(): JSX.Element {
        if (!this.state || this.state.totalFailedResults == null || this.state.uniqueFailingTests == null) {
            return <Spinner className="analytics-card-spinner flex-grow flex-column" size={SpinnerSize.large} />;
        } else {
            const { uniqueFailingTests, totalFailedResults, uniqueFlakyTests } = this.state;
            let messagePostFix = "";
            if (uniqueFailingTests === 0) {
                messagePostFix = Resources.TestsText;
            } else {
                messagePostFix = localeFormat(
                    Resources.TestsCausingFailedResultsFormat,
                    uniqueFailingTests === 1 ? Resources.TestAsPrefixSingularText : Resources.TestsText,
                    totalFailedResults != null ? totalFailedResults.toLocaleString() : "",
                    totalFailedResults === 1 ? Resources.ResultSingularText : Resources.ResultsPluralText
                );
            }

            return (
                <div className="top-failing-tests-section-data" id="failing-tests-count">
                    <span className={css("top-failing-tests-data-count", uniqueFailingTests ? "failures-exist" : "")}>
                        {uniqueFailingTests != null ? uniqueFailingTests.toLocaleString() : ""}
                    </span>
                    <span className="top-failing-tests-data-text fontSizeM">{messagePostFix}</span>
                    {this._getUniqueFlakyTestsComponent(uniqueFlakyTests)}
                </div>
            );
        }
    }

    private _getUniqueFlakyTestsComponent(uniqueFlakyTests: number | undefined): JSX.Element | null {
        if (!isShowFlakyTestsDetailsEnabled(this.context.pageContext) || uniqueFlakyTests === undefined) {
            return null;
        }
        return (
            <div className="unique-flaky-tests">
                <span className={css("top-flaky-tests-data-count", uniqueFlakyTests ? "failures-exist" : "")}>
                    {uniqueFlakyTests ? uniqueFlakyTests.toLocaleString() : 0}
                </span>
                <span className="top-flaky-tests-data-text font-size-m">{Resources.TestsCausingFlakyResultsFormat}</span>
            </div>
        )
    }

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getState());
    };
}

Components.add("FailedTestsMetricView", FailedTestsMetricView);
